import currencyJs from 'currency.js';

// src/domain/values/Money.ts
var ISO_4217_ALPHA = /^[A-Za-z]{3}$/;
var MINOR_UNITS_PER_MAJOR = 100;
function normaliseCurrency(code) {
  const trimmed = typeof code === "string" ? code.trim() : "";
  if (!ISO_4217_ALPHA.test(trimmed)) {
    throw new TypeError(
      `Expected a 3-letter ISO 4217 currency code, got ${JSON.stringify(code)}`
    );
  }
  return trimmed.toUpperCase();
}
var Money = class _Money {
  /**
   * Every factory funnels through here, so this is the one place a currency code is validated and
   * normalised — including the codes `add`/`subtract` carry forward, which cost a cheap re-check in
   * exchange for there being no unvalidated path in.
   *
   * @param amountMinor - The amount in integer minor units (e.g. cents).
   * @param currency - The currency code.
   * @throws {TypeError} If `currency` is not a three-letter ISO 4217 code.
   */
  constructor(amountMinor, currency) {
    this.amountMinor = amountMinor;
    this.currency = normaliseCurrency(currency);
  }
  /**
   * Creates a `Money` from a major-unit amount (e.g. dollars).
   *
   * Converted through `currency.js` rather than `Math.round(major * 100)`, which loses a cent on
   * any amount whose float representation falls just below the half-cent boundary: `1.005 * 100` is
   * `100.49999999999999`, so the naive form rounds $1.005 **down** to $1.00. `currency.js` rounds
   * the decimal representation and returns 101.
   *
   * @param major - Amount in major units; rounded to the nearest minor unit.
   * @param currency - The currency code.
   * @returns A new `Money`.
   */
  static of(major, currency) {
    return new _Money(currencyJs(major).intValue, currency);
  }
  /**
   * Parses a formatted amount — `"$1,234.56"`, `"1234.56"`, `"1.234,56"` with the matching
   * separators — into exact minor units.
   *
   * Delegates to `currency.js`, which strips the symbol and grouping and yields integer minor units
   * directly, so the string never passes through a lossy float.
   *
   * Throws rather than coercing, consistent with {@link Money.ofMinor}. `currency.js` alone would
   * turn `"not-a-price"` into `NaN` and `""` into `0` — and a silent `$0.00` is the worst possible
   * outcome for a price, since it looks like a real one all the way to the customer.
   *
   * @param input - The formatted amount.
   * @param currency - The currency code to tag the result with.
   * @param options - {@link MoneyParseOptions}, for locales that group and decimalise differently.
   * @returns A new `Money`.
   * @throws {TypeError} If `input` holds no parseable amount.
   * @example
   * Money.parse('$1,234.56', 'AUD').amountMinor;                                    // 123456
   * Money.parse('1.234,56', 'PHP', { separator: '.', decimal: ',' }).amountMinor;    // 123456
   */
  static parse(input, currency, options = {}) {
    const { separator = ",", decimal = "." } = options;
    const parsed = currencyJs(input, { separator, decimal });
    if (!Number.isFinite(parsed.intValue) || !/\d/.test(input)) {
      throw new TypeError(`Money.parse could not read an amount from "${input}"`);
    }
    return new _Money(parsed.intValue, currency);
  }
  /**
   * Creates a `Money` from an integer minor-unit amount (e.g. cents).
   *
   * @param minor - Amount in minor units.
   * @param currency - The currency code.
   * @returns A new `Money`.
   * @throws {TypeError} If `minor` is not an integer.
   */
  static ofMinor(minor, currency) {
    if (!Number.isInteger(minor)) {
      throw new TypeError(`Money.ofMinor expects an integer minor amount, got ${minor}`);
    }
    return new _Money(minor, currency);
  }
  /**
   * A zero amount in the given currency.
   *
   * @param currency - The currency code.
   * @returns A `Money` of `0`.
   */
  static zero(currency) {
    return new _Money(0, currency);
  }
  /** The amount in major units (e.g. dollars). */
  get major() {
    return this.amountMinor / MINOR_UNITS_PER_MAJOR;
  }
  /** Whether the amount is exactly zero. */
  get isZero() {
    return this.amountMinor === 0;
  }
  /**
   * Adds another amount of the same currency.
   *
   * @param other - The amount to add.
   * @returns A new `Money` with the summed amount.
   * @throws {TypeError} If `other` is a different currency.
   */
  add(other) {
    this.assertSameCurrency(other);
    return new _Money(this.amountMinor + other.amountMinor, this.currency);
  }
  /**
   * Subtracts another amount of the same currency.
   *
   * @param other - The amount to subtract.
   * @returns A new `Money` with the difference.
   * @throws {TypeError} If `other` is a different currency.
   */
  subtract(other) {
    this.assertSameCurrency(other);
    return new _Money(this.amountMinor - other.amountMinor, this.currency);
  }
  /**
   * Value-equality against another amount.
   *
   * @param other - The amount to compare.
   * @returns `true` if both the currency and the minor amount match.
   */
  equals(other) {
    return this.currency === other.currency && this.amountMinor === other.amountMinor;
  }
  /**
   * Orders this amount against another of the same currency, for use as a comparator.
   *
   * Unlike {@link Money.equals}, which answers "are these the same amount?" across any two
   * `Money` and so tolerates a currency mismatch, ordering two different currencies has no
   * meaningful answer — an exchange rate is not the SDK's to invent.
   *
   * @param other - The amount to compare against.
   * @returns `-1` when this is the smaller amount, `1` when it is the larger, `0` when equal.
   * @throws {TypeError} If `other` is a different currency.
   * @example
   * units.sort((a, b) => a.rate.compare(b.rate)); // cheapest first
   */
  compare(other) {
    this.assertSameCurrency(other);
    if (this.amountMinor < other.amountMinor) return -1;
    return this.amountMinor > other.amountMinor ? 1 : 0;
  }
  /**
   * Whether this amount is strictly less than another of the same currency.
   *
   * @param other - The amount to compare against.
   * @returns `true` if this amount is the smaller.
   * @throws {TypeError} If `other` is a different currency.
   */
  lessThan(other) {
    return this.compare(other) < 0;
  }
  /**
   * Whether this amount is strictly greater than another of the same currency.
   *
   * @param other - The amount to compare against.
   * @returns `true` if this amount is the larger.
   * @throws {TypeError} If `other` is a different currency.
   */
  greaterThan(other) {
    return this.compare(other) > 0;
  }
  /**
   * Formats the amount as a localized currency string, optionally with a billing-period suffix.
   *
   * @param options - {@link MoneyFormatOptions}.
   * @returns e.g. `"$249.00"`, or `"$249.00 / mo"` when `period` is given.
   */
  format(options = {}) {
    const { locale, period, periodSeparator = " / " } = options;
    const amount = new Intl.NumberFormat(locale, {
      style: "currency",
      currency: this.currency
    }).format(this.major);
    return period ? `${amount}${periodSeparator}${period}` : amount;
  }
  /**
   * Throws if `other` is a different currency.
   *
   * @param other - The amount to check against `this`.
   * @throws {TypeError} If the currencies differ.
   */
  assertSameCurrency(other) {
    if (other.currency !== this.currency) {
      throw new TypeError(
        `Cannot combine Money of different currencies: ${this.currency} and ${other.currency}`
      );
    }
  }
};

// src/domain/values/Dimensions.ts
var Dimensions = class {
  /**
   * @param length - Length (depth) in `unit`.
   * @param width - Width (frontage) in `unit`.
   * @param unit - Unit of measure.
   * @param height - Optional height in `unit`.
   */
  constructor(length, width, unit, height) {
    this.length = length;
    this.width = width;
    this.unit = unit;
    this.height = height;
  }
  /** The floor area (`length × width`) in square `unit`. */
  get area() {
    return this.length * this.width;
  }
  /**
   * Formats the footprint for display, length first by default — the order RapidStor storefronts
   * have always rendered — or width first via `order`.
   *
   * @param options - {@link DimensionsFormatOptions}.
   * @returns e.g. `"5m × 3m (15m²)"`, or `"5m × 3m"` when `showArea` is `false`.
   * @example
   * new Dimensions(5, 3, 'm').format();                      // "5m × 3m (15m²)"
   * new Dimensions(5, 3, 'ft').format({ areaUnit: 'sqft' }); // "5ft × 3ft (15sqft)"
   * new Dimensions(5, 3, 'm').format({ labelled: true, showArea: false }); // "5m L/3m W"
   */
  format(options = {}) {
    const {
      showArea = true,
      separator = " \xD7 ",
      areaUnit = `${this.unit}\xB2`,
      showUnit = true,
      labelled = false,
      order = "lengthFirst"
    } = options;
    const unit = showUnit ? this.unit : "";
    const [firstValue, firstLabel, secondValue, secondLabel] = order === "widthFirst" ? [this.width, "W", this.length, "L"] : [this.length, "L", this.width, "W"];
    const base = labelled ? `${firstValue}${unit} ${firstLabel}/${secondValue}${unit} ${secondLabel}` : `${firstValue}${unit}${separator}${secondValue}${unit}`;
    return showArea ? `${base} (${this.area}${areaUnit})` : base;
  }
};

// src/utils/size.ts
function formatSize(value, options = {}) {
  const {
    sizeSymbol = "m",
    areaSizeSymbol = "m\xB2",
    area = true,
    showSymbol = true,
    locale
  } = options;
  const formatted = value.toLocaleString(locale, {
    maximumFractionDigits: 2,
    trailingZeroDisplay: "stripIfInteger"
  });
  if (!showSymbol) return formatted;
  return `${formatted}${area ? areaSizeSymbol : sizeSymbol}`;
}
function formatDimensions(length, width, unit, options = {}) {
  const { height, ...formatOptions } = options;
  return new Dimensions(length, width, unit, height).format(formatOptions);
}

// src/domain/values/Address.ts
var Address = class _Address {
  /**
   * @param address1 - First address line.
   * @param address2 - Second address line (may be empty).
   * @param city - City / suburb.
   * @param region - State / region.
   * @param postalCode - Postal / ZIP code.
   * @param country - Country.
   */
  constructor(address1, address2, city, region, postalCode, country) {
    this.address1 = address1;
    this.address2 = address2;
    this.city = city;
    this.region = region;
    this.postalCode = postalCode;
    this.country = country;
  }
  /**
   * Builds an `Address` from the raw `rapidstor-types` address record.
   *
   * @param raw - The raw address.
   * @returns A new `Address`.
   */
  static from(raw) {
    return new _Address(
      raw.address1,
      raw.address2,
      raw.city,
      raw.region,
      raw.postalCode,
      raw.country
    );
  }
  /**
   * Formats the address as a single separated line, omitting blank/whitespace-only parts.
   *
   * @param options - {@link AddressFormatOptions}.
   * @returns e.g. `"12 Smith St, Fitzroy, VIC, 3065, Australia"`.
   */
  format(options = {}) {
    const { separator = ", ", includeCountry = true } = options;
    return [
      this.address1,
      this.address2,
      this.city,
      this.region,
      this.postalCode,
      includeCountry ? this.country : ""
    ].map((part) => (part ?? "").trim()).filter((part) => part !== "").join(separator);
  }
};
function monthlyToWeekly(monthlyValue) {
  return currencyJs(monthlyValue).multiply(12).divide(52).value;
}
function quarterMonth(monthlyValue) {
  return currencyJs(monthlyValue).divide(4).value;
}
function formatMoney(value, options = {}) {
  const { weekly = false, billingDefault, locale = "en-AU" } = options;
  let amount = Number.isFinite(value) ? value : 0;
  if (weekly) {
    amount = billingDefault === "28DayAnniversary" ? quarterMonth(amount) : monthlyToWeekly(amount);
  }
  return Math.abs(amount).toLocaleString(locale, {
    trailingZeroDisplay: "stripIfInteger",
    maximumFractionDigits: 2,
    minimumFractionDigits: 2
  });
}
function timeframeSuffix(options) {
  const { weekly, billingDefault, longForm = false } = options;
  if (weekly) {
    return billingDefault === "28DayAnniversary" ? longForm ? " per 4 weeks" : "/4wks" : longForm ? " per week" : "/wk";
  }
  return longForm ? " per month" : "/mo";
}
function conventionalSymbolPosition(locale, currency) {
  if (!currency) return "before";
  try {
    const formatted = new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      currencyDisplay: "code"
    }).format(1);
    return formatted.trimStart().startsWith(currency) ? "before" : "after";
  } catch {
    return "before";
  }
}
function formatPriceLabel(value, options = {}) {
  const {
    currencySymbol = "$",
    currencySymbolPosition = "none",
    currency,
    locale = "en-GB",
    shortForm = false
  } = options;
  const formatted = formatMoney(value, options);
  const suffix = shortForm ? "" : timeframeSuffix(options);
  const position = currencySymbolPosition === "none" ? conventionalSymbolPosition(locale, currency) : currencySymbolPosition;
  const amount = position === "after" ? `${formatted}${currencySymbol}` : `${currencySymbol}${formatted}`;
  return `${amount}${suffix}`;
}
function weeklyRate(monthly, options = {}) {
  const { billingDefault } = options;
  const weeklyMajor = billingDefault === "28DayAnniversary" ? quarterMonth(monthly.major) : monthlyToWeekly(monthly.major);
  return Money.of(weeklyMajor, monthly.currency);
}

// src/utils/deals.ts
function getDealDiscountType(deal) {
  if ((deal.discountPlan?.dcPCDiscount ?? 0) > 0) return "percentage";
  if ((deal.discountPlan?.dcChgAmt ?? 0) > 0) return "charge";
  if ((deal.discountPlan?.dcFixedDiscount ?? 0) > 0) return "fixed";
  return "none";
}
function isDealFree(deal) {
  return deal.discountPlan?.dcPCDiscount === 100 || deal.rates.discounted && deal.rates.discountedRate === 0;
}
function isDealPrepaid(deal) {
  return deal.discountPlan?.bPrepay === true || (deal.discountPlan?.iPrePaidMonths ?? 0) > 1 || deal.rates.prepaidDiscount === true;
}
function isDealChargeAmount(deal) {
  return (deal.discountPlan?.dcChgAmt ?? 0) > 0;
}
function findMatchingDeal(deals, dealId) {
  if (!dealId || !deals || deals.length === 0) return void 0;
  return deals.find((deal) => deal._id === dealId);
}
function getEffectiveDealRate(deal, rate) {
  if (isDealFree(deal) || isDealChargeAmount(deal)) return rate;
  return deal.rates.discountedRate ?? rate;
}

// src/utils/phrasing.ts
function getNumberWithOrdinalSuffix(num) {
  const pluralRules = new Intl.PluralRules("en-US", { type: "ordinal" });
  const suffixes = /* @__PURE__ */ new Map([
    ["one", "st"],
    ["two", "nd"],
    ["few", "rd"],
    ["other", "th"]
  ]);
  const rule = pluralRules.select(num);
  const suffix = suffixes.get(rule) ?? "th";
  return `${num}${suffix}`;
}
function ordinal(num) {
  return getNumberWithOrdinalSuffix(num);
}

export { Address, Dimensions, Money, findMatchingDeal, formatDimensions, formatMoney, formatPriceLabel, formatSize, getDealDiscountType, getEffectiveDealRate, getNumberWithOrdinalSuffix, isDealChargeAmount, isDealFree, isDealPrepaid, monthlyToWeekly, ordinal, weeklyRate };
//# sourceMappingURL=chunk-SJ2EGAN4.js.map
//# sourceMappingURL=chunk-SJ2EGAN4.js.map